/**
 * The corridor card manifest, and the rest of the landing media.
 *
 * ROTATION IS THE POINT, and this array is a rule rather than a set. Reorder it
 * and the properties below are gone, silently, with nothing to catch it.
 *
 * WHAT FLIES NOW. Every card with a recognisable face is out of the corridor
 * for good: the three laptop portraits and both trio scenes. The founder ruling
 * of 21 Aug is settled and this file is not the place to reopen it. What takes
 * their slots is the thing the product is actually about, drawn as live
 * terminal panels. The graded portraits still exist for the founders band far
 * down the page, where a still portrait at full size is correct and a flying
 * one was not.
 *
 * THE ROTATION, four beats repeated four times:
 *
 *     silhouette, panel, cloud, product screen
 *
 * and here is what those sixteen entries actually buy, walked rather than
 * assumed:
 *
 * 1. No two cards of a kind are ever adjacent. That is true by construction
 *    now, not by arithmetic: consecutive entries are consecutive beats, so they
 *    differ in kind, and there is nothing left to prove by hand. The old
 *    manifest needed a paragraph of counting here because nine of its
 *    seventeen cards had people in them and one human to human adjacency was
 *    forced. Nothing forces anything at sixteen.
 * 2. Sixteen is four fours, so the cycle closes. The wrap from entry 15 back to
 *    entry 0 is product screen then silhouette, which is the same beat change
 *    as every other join, so a rail may start anywhere and fly through it
 *    without a seam. The seventeen card version had to prove that no rail ever
 *    flew its wrap. That proof is deleted because the wrap is now ordinary.
 * 3. CloudCorridor offsets the left rail by floor(length / 2), which is eight
 *    here, so the pairing across the axis is (0,8) (1,9) ... (7,15) (8,0).
 *    Eight is a multiple of the four beat, so BOTH RAILS SIT ON THE SAME BEAT:
 *    a silhouette faces a silhouette, a panel faces a panel, a cloud faces a
 *    cloud, a product screen faces a product screen. That is the opposite of
 *    what the seventeen card version arranged, and it is fine here for a reason
 *    that version could not use: no card in the flight carries a readable face
 *    any more. Every one of those pairs is two DIFFERENT cards of one kind,
 *    prom against charlie, prompt against build, cloud-a against cloud-c, so
 *    nothing is ever mirrored against itself. Change the card count, or add a
 *    fifth silhouette, and this has to be re-walked: an offset that stops being
 *    a multiple of four pairs unlike kinds instead, and an offset that lands on
 *    a repeat doubles one image across the axis.
 * 4. Nine cards fly per rail at full density, so the right rail walks entries 0
 *    to 8 and the left walks 8 to 15 and then 0. Their union is all sixteen:
 *    nothing truncates, and every card is on screen at some depth.
 *
 * WHY FOUR OF THE CARDS ARE NOT FILES. A panel entry carries a panel id and no
 * src at all, because a panel is live DOM. It inherits the brand tokens, so a
 * direction or theme flip restyles all of them with no re export; it stays
 * crisp as a card grows from thumbnail to full bleed, which is the one thing
 * the corridor does to every card it flies; and it costs zero bytes in a
 * corridor that is already most of the fold budget. The names live in
 * src/lib/panels.ts so that this file, the panel components and the corridor
 * can agree on them without importing each other.
 *
 * GRADE DISCIPLINE, EXPRESSED AS FILES. Cloud plates carry no colour at all.
 * The four cards with people left in them are all backlit silhouettes, which
 * already measured at or under the chroma this direction asks for and were left
 * alone: the grade never ADDS colour back. The interior portraits that were
 * solved by a binary search on saturation are exactly the cards that just left
 * the flight, so that receipt now belongs to the founders band rather than to
 * the corridor. Product captures are cropped and resized and nothing else, and
 * the panels draw from tokens. The consequence is unchanged and is the whole
 * point: the only saturated pixels in the flight are status lamps, in the
 * product screens and inside the panels.
 *
 * FOUR OF THE CARDS ALSO MOVE. The three runners and the flying silhouette
 * carry a short ambient clip beside their still, named by `videoSrc` below. The
 * still is the clip's poster, so nothing on this page depends on a clip
 * existing, and the corridor decides on its own whether a given reader is ever
 * handed one. Why those four and not the other eight is argued on the field
 * itself; when a clip plays is argued in CloudCorridor.tsx.
 *
 * ALT TEXT IS RESOLVED AT RENDER, NOT AT IMPORT. Every string goes through t()
 * inside streamImages(), because a module scope t() would freeze the English
 * copy into the bundle and the Thai visitor would never see the catalogue. The
 * corridor is aria-hidden decoration today, so nothing reads these out loud,
 * but the still frames are not decoration and they use the same list. A panel
 * carries a line too: it is markup rather than an image, but a reader of the
 * still is owed the same sentence either way.
 *
 * The cloud layer plates are single channel greyscale, and the density lives in
 * the luminance channel rather than in alpha. WebP keeps alpha in a separate
 * plane that posterises a soft gradient into visible contour rings as soon as
 * it is compressed hard enough to fit the fold budget, while the same gradient
 * survives the lossy luminance path at about a fifth of the bytes. Consume them
 * with mask-mode: luminance.
 */

import { t } from '../../lib/i18n/t';
import type { PanelId } from '../../lib/panels';

/** The four kinds that arrive as a file. */
export type ImageKind = 'founder' | 'cloud' | 'product' | 'scene';

/** What a card is, which is also what treatment it was given. */
export type CardKind = ImageKind | 'panel';

interface CardCopy {
  /** English copy, and the key its translation is filed under. */
  alt: string;
}

/** A card that is a picture, and sometimes a short clip of one. */
export interface ImageCard extends CardCopy {
  kind: ImageKind;
  /** Path under public/, ready for src. */
  src: string;
  /**
   * The basename of an ambient clip for this card: no directory, no extension,
   * and nothing at all for a card that is meant to hold still.
   *
   * THE STILL ABOVE IS THE POSTER, WHICH IS THE WHOLE DESIGN. A clip is laid on
   * top of a card that already ships, so a file that is missing, still
   * rendering, refused by the codec or skipped by the play policy leaves
   * exactly the picture the corridor draws today. Nothing in this file waits on
   * the video pipeline and nothing breaks if a clip never lands.
   *
   * ONLY THE SILHOUETTES GET ONE. The four cards with a figure in them are the
   * only ones whose subject is motion in the first place: a person running is a
   * picture of a moment, and two seconds of it is the same picture with the
   * moment restored. The other two kinds are excluded on their own evidence
   * rather than on budget.
   *
   * A CLOUD CARD NEVER GETS A CLIP. Cloud plates carry no colour at all: they
   * are single channel pictures that take their tone from the ground they are
   * drawn on, which is what lets a direction or a theme flip restyle the whole
   * sky with no re-export. A clip would bake today's grade into pixels and
   * freeze it there, and the layer plates these are cut from are consumed as
   * luminance masks, which is a thing a video cannot be at all.
   *
   * A PRODUCT SCREEN NEVER GETS ONE EITHER. A screen in this flight is
   * evidence, and evidence has to be readable. Video compression spends its
   * bits on motion and takes them from high contrast edges, which is exactly
   * what interface type is made of, so a deploy list or a live web address
   * would arrive smeared at the one size a reader might actually read it at.
   * The screens hold still and stay sharp.
   */
  videoSrc?: string;
  panel?: never;
}

/** A card that is a live terminal panel, mounted by id and drawn from tokens. */
export interface PanelCard extends CardCopy {
  kind: 'panel';
  /** Which panel to mount. The catalogue is src/lib/panels.ts. */
  panel: PanelId;
  src?: never;
}

/**
 * A card is a file OR a panel, never both and never neither.
 *
 * It is a union rather than one interface with two optional fields because the
 * optional version lets a card be written with no src and no panel at all, and
 * the corridor would render that as an empty frame with nothing to say about
 * why. The `?: never` legs are what let a caller read `card.panel` without
 * narrowing first, which is what streamImages() and the corridor both want.
 */
export type StreamCard = ImageCard | PanelCard;

/**
 * Every card is exported at this size, and the number is a compromise with a
 * receipt rather than a preference.
 *
 * The ratio is the card box in src/lib/corridor.ts, 18cqw by 25cqw, so a card
 * fills its frame exactly and object-fit: cover crops nothing. The stills
 * arrive 3:4 and are centre cropped to it horizontally, which leaves the
 * headroom above every head untouched.
 *
 * The size is what the fold budget can afford. The nearest card that is still
 * whole inside the frame measures about 300 CSS px on a 1440 viewport and about
 * 400 on a 1920 one, so 2x wants 800. Nine cards at 800 wide cost 79KB and put
 * the corridor at 152KB against a 150KB budget, and dropping quality to close
 * that gap pushed two cards to a setting that bands the smooth night gradients,
 * which is a worse artefact than softness. So 720 it is: uniform quality 72 on
 * every card, no banding anywhere, and exactly 2x the 360px plates this set
 * replaces. The one card in the flight that is under sampled by it is the
 * nearest plate on a 2x display wider than about 1700px, and that card is mid
 * sweep and half out of frame when it gets there.
 *
 * The corridor's imagery now weighs about 100KB across twelve files, because
 * five face cards left and the four panels that replaced them are markup. The
 * headroom that buys belongs to the fold, not to a quality bump: the banding
 * argument above did not change.
 */
export const CARD_SIZE = { width: 720, height: 1000 } as const;

/**
 * Where the ambient clips live, written once.
 *
 * A card names its clip by basename, exactly as it names its still by a whole
 * path, and streamImages() joins the two at the moment of the call. The
 * corridor is therefore handed a path with the extension left off and decides
 * for itself which containers to try, which is a codec question. Where media
 * lives is not a codec question, so it stays here with every other path in the
 * catalogue.
 */
const VIDEO_DIR = '/media/corridor/video/';

/**
 * The catalogue, keyed by the name each card is known by.
 *
 * IT IS KEYED ON PURPOSE. The lean list below used to reach into the full
 * manifest by index, so every card that moved silently repointed a phone card
 * at whatever had shifted into that slot, and the reader of the diff could not
 * see it happen. Naming the cards once means both lists read as rotations of
 * names, a typo is a compile error, and the alt copy exists in exactly one
 * place. Declaration order here is the catalogue and NOT the rotation: the
 * arrays below decide what flies.
 */
const CARD = {
  'prom-running': {
    kind: 'founder',
    src: '/media/corridor/prom-running.webp',
    videoSrc: 'prom-running',
    alt: 'Prom, one of the three founders, running against the light.',
  },
  'march-running': {
    kind: 'founder',
    src: '/media/corridor/march-running.webp',
    videoSrc: 'march-running',
    alt: 'March, one of the three founders, running against the light.',
  },
  'charlie-running': {
    kind: 'founder',
    src: '/media/corridor/charlie-running.webp',
    videoSrc: 'charlie-running',
    alt: 'Charlie, one of the three founders, running against the light.',
  },
  // The fourth silhouette, and the only card in the flight with more than one
  // person in it. Its three figures are backlit to solid black and name nobody,
  // which is why it can hold the silhouette beat without being a portrait.
  'flying-silhouette': {
    kind: 'scene',
    src: '/media/corridor/flying-silhouette.webp',
    videoSrc: 'flying-silhouette',
    alt: 'Three figures walking on the cloud deck, far above the weather.',
  },
  'cloud-a': {
    kind: 'cloud',
    src: '/media/corridor/cloud-a.webp',
    alt: 'A high anvil cloud bank, seen from above and far away.',
  },
  'cloud-b': {
    kind: 'cloud',
    src: '/media/corridor/cloud-b.webp',
    alt: 'A thin streak of high cloud pulled sideways by the wind.',
  },
  'cloud-c': {
    kind: 'cloud',
    src: '/media/corridor/cloud-c.webp',
    alt: 'A storm tower, seen from beside it at height.',
  },
  'cloud-d': {
    kind: 'cloud',
    src: '/media/corridor/cloud-d.webp',
    alt: 'A break in the cloud deck, dark underneath.',
  },
  'overview': {
    kind: 'product',
    src: '/media/corridor/overview.webp',
    alt: 'A project overview saying that nothing has gone wrong today.',
  },
  'deploys': {
    kind: 'product',
    src: '/media/corridor/deploys.webp',
    alt: 'The deploys screen, with the newest deploy marked live.',
  },
  'fix-request': {
    kind: 'product',
    src: '/media/corridor/fix-request.webp',
    alt: 'A build failure, with the fix request already written out.',
  },
  // The same web address the hero types in chapter 1, which chapter 5 then
  // shows again at plate size. A visitor who reads the corridor closely is
  // being handed the same promise twice.
  'site-live': {
    kind: 'product',
    src: '/media/corridor/site-live.webp',
    alt: 'A web address with a live pill beside it, deployed seconds ago.',
  },
  'panel-prompt': {
    kind: 'panel',
    panel: 'panel-prompt',
    alt: 'A sentence typing itself at a prompt, asking for a recipe site.',
  },
  'panel-agent': {
    kind: 'panel',
    panel: 'panel-agent',
    alt: 'An agent checking off its own plan, one item at a time.',
  },
  'panel-build': {
    kind: 'panel',
    panel: 'panel-build',
    alt: 'Named build steps ticking off while a bar fills.',
  },
  'panel-deploy': {
    kind: 'panel',
    panel: 'panel-deploy',
    alt: 'Log lines streaming until a live web address lands.',
  },
  // The four panels below do not fly. They are here because a panel with no alt
  // copy is a panel nobody can mount without inventing a sentence first, and
  // three of them are the sanctioned swaps.
  'panel-fix': {
    kind: 'panel',
    panel: 'panel-fix',
    alt: 'An error, with the fix request written out ready to copy.',
  },
  'panel-rows': {
    kind: 'panel',
    panel: 'panel-rows',
    alt: 'Table rows arriving one by one until six tables are ready.',
  },
  'panel-lamp': {
    kind: 'panel',
    panel: 'panel-lamp',
    alt: 'A health band settling on everything being fine.',
  },
  'panel-ship': {
    kind: 'panel',
    panel: 'panel-ship',
    alt: 'A commit, a green lamp, and the seconds since it deployed.',
  },
} satisfies Record<string, StreamCard>;

/**
 * The sixteen, in rotation order: four beats, four times over.
 *
 * Read it in rows of four and the rule is visible on the page. Every property
 * proved in the header comment depends on this shape, so a card added here
 * without its three companions breaks the beat, and the offset argument goes
 * with it.
 */
export const STREAM_MANIFEST: readonly StreamCard[] = [
  CARD['prom-running'], CARD['panel-prompt'], CARD['cloud-a'], CARD['overview'],
  CARD['march-running'], CARD['panel-agent'], CARD['cloud-b'], CARD['deploys'],
  CARD['charlie-running'], CARD['panel-build'], CARD['cloud-c'], CARD['fix-request'],
  CARD['flying-silhouette'], CARD['panel-deploy'], CARD['cloud-d'], CARD['site-live'],
];

/**
 * The panels held back from the flight, ready to swap in.
 *
 * They are exported rather than left implicit so that a designer trying a
 * different beat can find them in one grep, and so that nobody re-authors a
 * panel that already exists. Swapping is a one line edit in the manifest above:
 * take out a panel entry, put one of these in its slot, and the rotation and
 * every property that rests on it are untouched, because a panel for a panel is
 * the same beat.
 *
 * `panel-fix` is deliberately not among them. The corridor already flies the
 * real fix request screen, and a panel that redraws a screen the flight is
 * carrying as evidence is the one panel that would be decoration.
 */
export const PANEL_SWAPS: readonly PanelCard[] = [
  CARD['panel-rows'],
  CARD['panel-lamp'],
  CARD['panel-ship'],
];

/**
 * Mobile flies nine, because sixteen cards on a 375px rail is a smear rather
 * than a stream.
 *
 * NINE IS NOT A MULTIPLE OF FOUR, so the strict four beat cannot close and this
 * list cannot simply be the first nine of the manifest. It is solved for three
 * properties instead, and the third one is new: this order was re-walked on 22
 * Aug because the old one read correctly in flight and composed badly at rest,
 * which nobody had checked because the two readings use the same list in two
 * different ways.
 *
 * 1. THE COMPOSED STILL. CorridorStill slices this list in threes, near to far:
 *    the right rail is entries 0 to 2 and the left rail is entries 3 to 5, so
 *    entry 0 and entry 3 are the two cards at reading size, one on each side of
 *    the copy. The old order put prom at 0 and march at 3, which flanked the
 *    headline with two runner silhouettes at the same size in the same pose,
 *    and a reader who asked for no motion met that frame and nothing else.
 *
 *    Now entry 0 is the live web address and entry 3 is a runner, so the near
 *    pair is the thing that shipped against the person who shipped it. Walk the
 *    whole frame: the right rail is the live address at reading size, a runner
 *    behind it, the first prompt at the back; the left rail is a runner at
 *    reading size, the build behind him, a cloud plate at the back. Both rails
 *    carry a person, no two cards of a kind are stacked on one rail, and no two
 *    cards of a kind face each other at any of the three depths. Follow only
 *    the three cards that are the product itself and they run toward the
 *    reader in order: the first prompt at the deepest stop, the build one stop
 *    nearer, the live web address at reading size.
 * 2. No two cards of a kind are adjacent, the wrap from entry 8 back to entry 0
 *    included, because the phone rail flies eight of the nine and walks that
 *    wrap on the left rail. Read the kinds round the cycle: product,
 *    silhouette, panel, silhouette, panel, cloud, product, cloud, silhouette,
 *    and back to product. Every join changes kind.
 * 3. The left rail is offset by floor(9 / 2), which is four, and eight cards
 *    fly per rail, so the pairs that ever share a depth are (0,4) (1,5) (2,6)
 *    (3,7) (4,8) (5,0) (6,1) and (7,2). All eight are two different kinds. No
 *    pair on the phone is ever two cards of one kind, which is a stronger
 *    result than the desktop sixteen manages.
 *
 * WHY ENTRY 3 IS THE RUNNER AND ENTRY 0 IS NOT, which is arithmetic rather than
 * taste and is the part to re-walk if this list is ever reordered again. A card
 * at position i sits beside i + 1 and faces i + 4. Take those two rules at face
 * value and the three runners have to be pairwise apart by 2, 3, 6 or 7
 * positions, and of the nine positions the only triples that manage it are
 * {i, i+3, i+6}. Every one of those holds 0 if and only if it holds 3, and 0
 * and 3 are exactly the two reading size slots, so under that reading a runner
 * at reading size forces a runner opposite it, which is the defect this order
 * was walked to fix. Two runners flanking the headline, or no runner at reading
 * size at all: those look like the only two answers.
 *
 * They are not, because one offset-four pair never actually shares a depth.
 * Only eight of the nine entries fly per rail, so entry 3 is on the right rail
 * only and entry 8 is on the left rail only, and {3,8} is the one pair that is
 * never on screen together. Runners at 1, 3 and 8 therefore keep every property
 * above while putting a person at reading size. That is this list, and it is
 * also why the runner at reading size is on the LEFT rail, mirrored from the
 * desktop still: position 3 is the only reading size slot a runner can hold, so
 * there is no second arrangement to prefer.
 *
 * All of which turns on eight cards flying per rail. Raise DENSITY.lean.cards
 * to nine and {3,8} starts sharing a depth, the escape closes, and this order
 * has to be walked again.
 *
 * Eight cards fly per rail at lean density, so the right rail walks entries 0
 * to 7 and the left walks 4 to 8 and then 0 to 2. Their union is all nine.
 *
 * WHAT IT KEEPS AND WHY. All three runner silhouettes, because at 180px a face
 * is a smudge and a silhouette is still a person, and they name one man each
 * rather than leaning on a group shot to cover all three. Two panels, the first
 * prompt and the build, which are the two beats that read at thumbnail size
 * because each is one line of type and one bar. The product screens are the
 * two least dense captures: fix-request is the busiest frame in the set and it
 * is the first thing that goes illegible on a phone rail. site-live takes the
 * reading size slot of the two, because one address beside one lamp is the only
 * product frame in the set that survives being shrunk.
 */
export const STREAM_MANIFEST_LEAN: readonly StreamCard[] = [
  CARD['site-live'],
  CARD['march-running'],
  CARD['panel-prompt'],
  CARD['prom-running'],
  CARD['panel-build'],
  CARD['cloud-a'],
  CARD['deploys'],
  CARD['cloud-b'],
  CARD['charlie-running'],
];

/**
 * The parallax plates, far to near. Depth is already baked into each file as
 * amplitude and softness, so the far plate is flatter and hazier before CSS
 * touches it. The layer opacity, blur and mix toward the page ground still come
 * from tokens, because a plate must never name a colour.
 */
export interface CloudLayer {
  src: string;
  width: number;
  height: number;
  /** 0 is furthest, 1 is nearest. Drives opacity, blur and the depth mix. */
  depth: number;
  /** The far three wrap horizontally, so a wide desktop cannot show a join. */
  tiles: boolean;
}

export const CLOUD_LAYERS: readonly CloudLayer[] = [
  { src: '/media/clouds/cloud-far.webp', width: 1600, height: 900, depth: 0, tiles: true },
  { src: '/media/clouds/cloud-mid.webp', width: 1400, height: 800, depth: 0.33, tiles: true },
  { src: '/media/clouds/cloud-near.webp', width: 1400, height: 500, depth: 0.66, tiles: true },
  { src: '/media/clouds/cloud-vapor.webp', width: 900, height: 300, depth: 1, tiles: false },
];

/**
 * What the corridor and the stills actually consume: a card with its copy
 * translated at the moment of the call.
 *
 * It is not the corridor's own StreamImage because that type is an image and
 * only an image, and a panel slide has no src to give it. The corridor widens
 * its prop to this shape rather than this file narrowing to that one, because
 * the manifest is where the src or panel decision is made and it should not be
 * made twice.
 */
export type StreamSlide =
  | { src: string; videoStem?: string; panel?: never; alt: string }
  | { src?: never; videoStem?: never; panel: PanelId; alt: string };

/**
 * Cards ready for the corridor, translated at the moment of the call.
 * Pass STREAM_MANIFEST_LEAN on a narrow viewport.
 *
 * The clip basename becomes a path here, for the same reason the alt copy
 * becomes a sentence here: a card names itself once, and the shape a consumer
 * wants is built when the consumer asks. The corridor receives a stem with no
 * extension on it and appends the containers it is willing to try.
 *
 * Three literals rather than one with a conditional spread, because the return
 * type is a union with `?: never` legs and a spread makes the compiler guess
 * which leg it is looking at. Written out, nothing is inferred.
 */
export function streamImages(cards: readonly StreamCard[] = STREAM_MANIFEST): StreamSlide[] {
  return cards.map((card): StreamSlide =>
    card.kind === 'panel'
      ? { panel: card.panel, alt: t(card.alt) }
      : card.videoSrc
        ? { src: card.src, videoStem: `${VIDEO_DIR}${card.videoSrc}`, alt: t(card.alt) }
        : { src: card.src, alt: t(card.alt) },
  );
}

/*
 * founderCards() USED TO LIVE HERE AND IT IS DELETED RATHER THAN REWIRED.
 *
 * Its docblock said it was what the reduced motion still composes at reading
 * size, and that was false twice over: nothing imported it, and CorridorStill
 * takes the same translated slide list the moving corridor takes, sliced three
 * per rail, so a founder-only list is not a shape anything in this codebase
 * wants. It also filtered on kind === 'founder', which quietly dropped
 * flying-silhouette, the fourth silhouette in the rotation. A helper that is
 * uncalled AND wrong about itself is worse than no helper, because the next
 * reader trusts the sentence and not the call sites. The composition it claimed
 * to serve is argued on STREAM_MANIFEST_LEAN above and in LandingStills.tsx.
 */
